import React from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import './UnifiedTvAudiencePreviewWidget.scss';
import Widget from '../../../../common/Widget/Widget';
import AudiencePreview from '../AudiencePreview/AudiencePreview';
import AudienceTarget from '../AudienceTarget/AudienceTarget';
import SubmitButton from '../../../../common/SubmitButton/SubmitButton';
import Separator from '../../../../common/Separator/Separator';
import { createUnifiedPromiseEvaluatedToComponentProps } from '../AudiencePreviewWidget/UnifiedTvAudiencePreviewWidgetUtils';

const UnifiedTvAudiencePreviewWidget = (props) => {
  const audienceTarget = props.isActivateVisible ? (
    <AudienceTarget
      marketsAndAdvertisersPromise={props.marketsAndAdvertisersPromise}
      audienceId={props.audienceId}
      audienceName={props.audienceName}
      audienceSegment={props.audienceSegment}
      isActivateEnabled={props.isActivateEnabled}
      isActivateAudienceEnabled={props.isActivateAudienceEnabled}
      isActivateAudienceVisible={props.isActivateAudienceVisible}
      isActivateTargetEnabled={props.isActivateTargetEnabled}
      activateAudienceDisabledText={props.activateAudienceDisabledText}
      channel={props.channel}
      getSegmentIds={props.getSegmentIds}
      createAudienceTargetTaxonomy={props.createAudienceTargetTaxonomy}
      createAudienceTargetUserList={props.createAudienceTargetUserList}
      createAmplifiedAudience={props.createAmplifiedAudience}
      createAlwaysOnAudience={props.createAlwaysOnAudience}
      createDeterministicAudience={props.createDeterministicAudience}
      createUserListForDeterministicAudience={props.createUserListForDeterministicAudience}
      createUnifiedTvActivation={props.createUnifiedTvActivation}
      notificator={props.notificator}
      updateAudienceDeterministicActivated={props.updateAudienceDeterministicActivated}
      updateAudienceAmplifiedActivated={props.updateAudienceAmplifiedActivated}
      updateAudienceAlwaysOnActivated={props.updateAudienceAlwaysOnActivated}
      dspService={props.dspService}
      isAudienceDeterministicActivated={props.isAudienceDeterministicActivated}
      isAudienceAmplifiedActivated={props.isAudienceAmplifiedActivated}
      isAudienceAlwaysOnActivated={props.isAudienceAlwaysOnActivated}
      activatedAmplifiedThreshold={props.activatedAmplifiedThreshold}
      activatedAlwaysOnThreshold={props.activatedAlwaysOnThreshold}
      activatedMarket={props.activatedMarket}
      activatedAdvertiser={props.activatedAdvertiser}
      activatedDataContractId={props.activatedDataContractId}
      activatedDataContractText={props.activatedDataContractText}
      activatedCategoryId={props.activatedCategoryId}
      ssoService={props.ssoService}
      getAmplifiedEstimatedReachGoal={props.getAmplifiedEstimatedReachGoal}
      handleActivatingAudience={props.handleActivatingAudience}
      onUnifiedAudiencesActivated={props.onUnifiedAudiencesActivated}
      handleActivatingTarget={props.handleActivatingTarget}
      hasAmplificationModeSelector={props.hasAmplificationModeSelector}
      getDataContract={props.getDataContract}
      getTaxonomyCategory={props.getTaxonomyCategory}
      getAmpModelCategory={props.getAmpModelCategory}
      isDeterministicDynamicAllowed={props.isDeterministicDynamicAllowed}
      isContextualUniquenessError={props.isContextualUniquenessError}
      allowDeterministicSegment={props.allowDeterministicSegment}
      allowCustomPriceSegment={props.allowCustomPriceSegment}
      allowOptimizeSegment={props.allowOptimizeSegment}
    />
  ) : undefined;

  const renderPreview = (sourceResults) => (
    <div className="unified-tv-audience-preview-component">
      <span className="unified-preview-label">Audience Preview</span>
      <div className="unified-source-rows">
        {sourceResults.map((source) => (
          <AudiencePreview
            key={source.sourceValue}
            {...source}
            sourceLabel={source.sourceLabel}
            sourceColorClass={source.sourceColorClass}
            hideTitle={true}
            hideExploreButton={true}
            showAudienceIpCount={props.showAudienceIpCount}
          />
        ))}
      </div>
      <div className="unified-controls">
        {audienceTarget}
        {audienceTarget && <Separator />}
        <SubmitButton
          label="Explore Audience"
          isDisabled={true}
          onClick={props.onExploreAudience}
          dataTestId="audience-builder-explore-button"
        />
      </div>
    </div>
  );

  const emptySources = props.sources.map((s) => ({
    sourceValue: s.value,
    sourceLabel: s.label,
    sourceColorClass: s.colorClass,
    hasData: false,
  }));

  return (
    <Widget
      name="UnifiedTvAudiencePreviewWidget"
      dataService={createUnifiedPromiseEvaluatedToComponentProps}
      dataServiceParams={[
        props.sources,
        props.audienceSegment,
        props.userId,
        props.audienceDistributionService,
        props.isRequestCancelledService,
        props.trackTooNarrow,
        props.hideAudiencePreview,
        props.householdDistributionService,
        props.showAudienceIpCount,
      ]}
      renderOnValueChange={[
        props.isActivateEnabled,
        props.isActivateVisible,
        props.isActivateAudienceEnabled,
        props.activateAudienceDisabledText,
        props.isActivateAudienceVisible,
        props.isActivateTargetEnabled,
        props.isAudienceDeterministicActivated,
        props.isAudienceAmplifiedActivated,
        props.isContextualUniquenessError,
      ]}
      onRender={(res) => renderPreview(res.sources)}
      style={{ height: '144px' }}
      isSmallSpinner={true}
      onDataLoaded={(data) => props.onAudienceDataLoaded(data.hasData)}
      emptyStateRender={() => renderPreview(emptySources)}
    />
  );
};

UnifiedTvAudiencePreviewWidget.propTypes = {
  sources: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string, colorClass: PropTypes.string })
  ).isRequired,
  audienceDistributionService: PropTypes.func.isRequired,
  householdDistributionService: PropTypes.func.isRequired,
  isRequestCancelledService: PropTypes.func.isRequired,
  audienceSegment: PropTypes.array,
  channel: PropTypes.any,
  userId: PropTypes.number.isRequired,
  trackTooNarrow: PropTypes.func.isRequired,
  onExploreAudience: PropTypes.func,
  onAudienceDataLoaded: PropTypes.func,
  hideAudiencePreview: PropTypes.bool,
  showAudienceIpCount: PropTypes.bool,
  marketsAndAdvertisersPromise: PropTypes.object,
  audienceId: PropTypes.string,
  audienceName: PropTypes.string,
  isActivateEnabled: PropTypes.bool,
  isActivateVisible: PropTypes.bool,
  isActivateAudienceEnabled: PropTypes.bool,
  isActivateAudienceVisible: PropTypes.bool,
  isActivateTargetEnabled: PropTypes.bool,
  activateAudienceDisabledText: PropTypes.string,
  getSegmentIds: PropTypes.func,
  createAudienceTargetTaxonomy: PropTypes.func,
  createAudienceTargetUserList: PropTypes.func,
  createAmplifiedAudience: PropTypes.func,
  createAlwaysOnAudience: PropTypes.func,
  createDeterministicAudience: PropTypes.func,
  createUserListForDeterministicAudience: PropTypes.func,
  createUnifiedTvActivation: PropTypes.func,
  notificator: PropTypes.object,
  dspService: PropTypes.object,
  updateAudienceDeterministicActivated: PropTypes.func,
  updateAudienceAmplifiedActivated: PropTypes.func,
  updateAudienceAlwaysOnActivated: PropTypes.func,
  isAudienceDeterministicActivated: PropTypes.bool,
  isAudienceAmplifiedActivated: PropTypes.bool,
  isAudienceAlwaysOnActivated: PropTypes.bool,
  activatedAmplifiedThreshold: PropTypes.number,
  activatedAlwaysOnThreshold: PropTypes.number,
  activatedMarket: PropTypes.object,
  activatedAdvertiser: PropTypes.object,
  activatedDataContractId: PropTypes.number,
  activatedDataContractText: PropTypes.string,
  activatedCategoryId: PropTypes.number,
  ssoService: PropTypes.object,
  getAmplifiedEstimatedReachGoal: PropTypes.func.isRequired,
  handleActivatingAudience: PropTypes.func.isRequired,
  onUnifiedAudiencesActivated: PropTypes.func,
  handleActivatingTarget: PropTypes.func.isRequired,
  hasAmplificationModeSelector: PropTypes.bool,
  getDataContract: PropTypes.func,
  getTaxonomyCategory: PropTypes.func,
  getAmpModelCategory: PropTypes.func,
  isDeterministicDynamicAllowed: PropTypes.bool,
  isContextualUniquenessError: PropTypes.bool,
  allowDeterministicSegment: PropTypes.bool,
  allowCustomPriceSegment: PropTypes.bool,
  allowOptimizeSegment: PropTypes.bool,
};

UnifiedTvAudiencePreviewWidget.defaultProps = {
  onAudienceDataLoaded: noop,
  onExploreAudience: noop,
  isActivateEnabled: false,
  isActivateVisible: false,
  isActivateAudienceEnabled: false,
  isActivateTargetEnabled: false,
  isActivateAudienceVisible: false,
};

export default UnifiedTvAudiencePreviewWidget;
